import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import styled from 'styled-components';
import { useTranslations } from '../../hooks/use_translations.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { noop } from '../../utils/util.js';
import { checkboxComponents, Checkbox } from '../Checkbox/Checkbox.js';
import { IconFavoriteSelected } from '../Icons/IconFavoriteSelected.js';
import { IconFavoriteUnselected } from '../Icons/IconFavoriteUnselected.js';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["disabled", "legend", "onChange", "readOnly", "size", "value"];
var gapSize = {
  16: 5,
  20: 5,
  24: 8,
  32: 10
};
var RatingsContainer = styled.div.withConfig({
  displayName: "Ratings__RatingsContainer",
  componentId: "sc-ystkw0-0"
})(["&&{color:", ";cursor:", ";display:inline-flex;position:relative;", ";}&& ", "{position:relative;display:inherit;margin-inline-end:", "px;}&& ", ":last-of-type{margin-inline-end:0;}"], function (p) {
  return p.theme.neutralColors.smoke;
}, function (p) {
  if (p.$disabled) return "not-allowed";
  if (p.$readOnly) return "default";
  return "pointer";
}, function (p) {
  return p.$readOnly && "\n        ".concat(checkboxComponents.CheckboxWrapper, " {\n            cursor: default;\n        }\n\n        svg: {\n            fill: ").concat(p.theme.neutralColors.rhino, ";\n            stroke: ").concat(p.theme.neutralColors.rhino, ";\n        }\n    ");
}, checkboxComponents.CheckboxWrapper, function (p) {
  return gapSize[p.$size];
}, checkboxComponents.CheckboxWrapper);
var Ratings = /*#__PURE__*/React.forwardRef(function Component(props, ref) {
  var _useThemeContext = useThemeContext(),
    localization = _useThemeContext.localization;
  var _useTranslations = useTranslations(localization === null || localization === void 0 ? void 0 : localization.translations),
    t = _useTranslations.t;
  var disabled = props.disabled,
    _props$legend = props.legend,
    legend = _props$legend === void 0 ? t("Ratings.defaultLegend") : _props$legend,
    _props$onChange = props.onChange,
    onChange = _props$onChange === void 0 ? noop : _props$onChange,
    readOnly = props.readOnly,
    _props$size = props.size,
    size = _props$size === void 0 ? 16 : _props$size,
    _props$value = props.value,
    value = _props$value === void 0 ? 0 : _props$value,
    rest = _objectWithoutProperties(props, _excluded);
  var _React$useState = React.useState(0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    hoverIndex = _React$useState2[0],
    setHoverIndex = _React$useState2[1];
  var onFieldsetLeave = function onFieldsetLeave() {
    return setHoverIndex(0);
  };
  return /*#__PURE__*/jsx(RatingsContainer, _objectSpread(_objectSpread({
    ref: ref,
    $size: size,
    $disabled: disabled,
    $readOnly: readOnly
  }, rest), {}, {
    children: /*#__PURE__*/jsxs("fieldset", {
      onBlur: readOnly ? void 0 : onFieldsetLeave,
      onMouseLeave: onFieldsetLeave,
      children: [/*#__PURE__*/jsx(VisuallyHidden, {
        children: /*#__PURE__*/jsx("legend", {
          children: legend
        })
      }), [1, 2, 3, 4, 5].map(function (i) {
        var onCheckboxChange = function onCheckboxChange() {
          return onChange(i);
        };
        var onCheckboxEnter = function onCheckboxEnter() {
          return setHoverIndex(i);
        };
        var checked = Math.max(hoverIndex, Math.round(value)) >= i;
        return /*#__PURE__*/jsx(Checkbox, {
          "aria-label": t("Ratings.label", {
            ratingString: i.toString()
          }),
          checked: readOnly ? void 0 : checked,
          defaultChecked: readOnly ? checked : void 0,
          disabled: disabled,
          icon: IconFavoriteUnselected,
          iconChecked: IconFavoriteSelected,
          name: "ratings",
          onChange: readOnly ? void 0 : onCheckboxChange,
          onFocus: readOnly ? void 0 : onCheckboxEnter,
          onMouseEnter: readOnly ? void 0 : onCheckboxEnter,
          readOnly: readOnly,
          size: size,
          type: "radio"
        }, i);
      })]
    })
  }));
});
Ratings.displayName = "Ratings";

export { Ratings };
